import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AsyncThunkAction, unwrapResult } from '@reduxjs/toolkit';
import {
  Button,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
} from '@chakra-ui/react';
import { userHasRequiredPermissions } from 'hooks';
import { FiTrash2 } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const TOAST_PROPS = {
  position: 'bottom' as const,
  isClosable: true,
};

export type DeleteButtonProps = {
  action?: AsyncThunkAction<any, any, any>;
  mutation?: () => Promise<unknown>;
  permissions?: string[];
  redirectTo?: string;
  entityName: string;
  isDisabled?: boolean;
};

const DeleteButton = ({
  action,
  mutation,
  permissions = [],
  redirectTo,
  entityName,
  isDisabled,
}: DeleteButtonProps): JSX.Element | null => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const toast = useToast();
  const history = useHistory();
  const hasPermissions = userHasRequiredPermissions(permissions);

  const [isRequestPending, setIsRequestPending] = React.useState(false);
  const [isAlertOpen, setIsAlertOpen] = React.useState(false);
  const onClose = () => setIsAlertOpen(false);

  const cancelActionRef = React.useRef<HTMLButtonElement>(null);
  const mountedComponentRef = React.useRef(true);

  const handleClick = () => setIsAlertOpen(true);
  const handleDeleteAction = async () => {
    setIsRequestPending(true);
    try {
      if (mutation) {
        await mutation();
      } else {
        unwrapResult(await dispatch(action));
      }

      if (mountedComponentRef.current) setIsRequestPending(false);
      toast({
        ...TOAST_PROPS,
        title: t('success', 'Sukces'),
        description: t(
          'entityNameWasDeleted',
          '{{entityName}} został usunięty.',
          { entityName },
        ),
        status: 'success',
        duration: 3000,
      });
      if (redirectTo) history.push(redirectTo);
      if (mountedComponentRef.current) onClose();
    } catch (error) {
      if (mountedComponentRef.current) setIsRequestPending(false);
      toast({
        ...TOAST_PROPS,
        title: t('errorOccurred', 'Wystąpił błąd'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
      });
    }
  };

  // zmiany w state po usunięciu zasobu mogą spowodować, że komponent przestanie być renderowany
  // poniższy workaround zapobiega wyciekowi pamięci
  React.useEffect(() => {
    mountedComponentRef.current = true;
    return () => {
      mountedComponentRef.current = false;
    };
  }, []);
  if (!hasPermissions) return null;

  const label = t('deleteEntityName', 'Usuń {{entityName}}', { entityName });

  return (
    <>
      <Tooltip label={label} hasArrow placement='top'>
        <span>
          <IconButton
            aria-label={label}
            icon={<FiTrash2 />}
            onClick={handleClick}
            isDisabled={isDisabled}
          />
        </span>
      </Tooltip>

      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelActionRef}
        onClose={onClose}
        closeOnOverlayClick={!isRequestPending}
        closeOnEsc={!isRequestPending}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='xl' fontWeight='bold'>
              {label}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t(
                'areYouSureThisProcessCannotBeReversed',
                'Czy jesteś pewien? Procesu tego nie można odwrócić.',
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelActionRef}
                onClick={onClose}
                isDisabled={isRequestPending}
                minWidth='12ch'
              >
                {t('cancel', 'Anuluj')}
              </Button>
              <Button
                colorScheme='red'
                onClick={handleDeleteAction}
                ml={3}
                isLoading={isRequestPending}
                minWidth='12ch'
                leftIcon={<FiTrash2 />}
              >
                {t('delete', 'Usuń')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default DeleteButton;
